import {
  collectQuotesInStructuredContent,
  stringifyStructuredContentPhrasingRunAsPlainText,
} from "@repo/app-types/content/structured/helpers";
import { parseStructuredContentFromModelOutput } from "@repo/app-types/content/structured/parser";
import type { StructuredContentRoot } from "@repo/app-types/content/structured/types";
import type {
  AccountGradingRecording,
  GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import type { QuoteParagraph } from "@repo/app-types/helpers/gradeValidationHelpers";
import { isNotUndefined } from "@repo/app-types/helpers/utilities";
import { fetchChatCompletion } from "@repo/ui/chat/api";
import type {
  ClientAssistantChatMessage,
  ClientUserChatMessage,
} from "@repo/ui/chat/types";
import { trimPartialTags } from "@repo/ui/markdown/tagPlugin";
import * as Sentry from "@sentry/react";
import { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useLazyTeammateCopilotMessagesQuery } from "../../api/entities/teammate.js";
import {
  apiBaseUrl,
  getApiAuthorizationHeaderValue,
} from "../../api/shared.js";
import { useAppSelector } from "../../hooks/hook.js";
import { selectTeammate } from "../teammate/teammateSlice.js";
import { QuoteMatcher, type QuoteMatchMap } from "./gradeValidationHelpers.js";

export type ClientAssistantChatMessageWithStructuredContent =
  ClientAssistantChatMessage & {
    structuredContent: StructuredContentRoot;
    quotes: readonly QuoteParagraph[];
  };

export type ClientChatMessageWithStructuredContent =
  | ClientUserChatMessage
  | ClientAssistantChatMessageWithStructuredContent;

interface BaseCompletionRequest {
  type: string;
}

interface InitialCompletionRequest extends BaseCompletionRequest {
  type: "initial";
}

interface UserMessageCompletionRequest extends BaseCompletionRequest {
  type: "user_message";
  message: ClientUserChatMessage;
}

type CompletionRequest =
  | InitialCompletionRequest
  | UserMessageCompletionRequest;

interface UseTeamPerformanceCopilotParams {
  gradingId: GradingId | undefined;
  sortedRecordings: readonly AccountGradingRecording[];
}

interface UseTeamPerformanceCopilotResult {
  visibleMessages: readonly ClientChatMessageWithStructuredContent[];
  quoteMatches: QuoteMatchMap;
  isLoading: boolean;
  composerValue: string;
  setComposerValue: (value: string) => void;
  onMessageSend: (message: string) => void;
  onMessageRetry: (messageId: string) => void;
}

export function useTeamPerformanceCopilot(
  params: UseTeamPerformanceCopilotParams,
): UseTeamPerformanceCopilotResult {
  const { gradingId, sortedRecordings } = params;

  const currentTeammate = useAppSelector(selectTeammate);

  const [messages, setMessages] = useState<
    ClientChatMessageWithStructuredContent[]
  >([]);

  const [completionRequests, setCompletionRequests] = useState<
    readonly CompletionRequest[]
  >([]);

  const [composerValue, setComposerValue] = useState("");

  // Lazy fetch current messages for this teammate chat
  const [fetchTeammateCopilotMessages] = useLazyTeammateCopilotMessagesQuery();

  const currentTeammateId = currentTeammate?.id;

  // Populate messages when "teammateCopilotMessages" is available
  useEffect(() => {
    if (!gradingId) {
      return;
    }

    const fetchMessages = async () => {
      if (currentTeammateId) {
        try {
          const teammateChatMessages = await fetchTeammateCopilotMessages({
            teammateId: currentTeammateId,
            chatDetails: {
              type: "grading",
              grading_id: gradingId,
            },
          }).unwrap();

          const messages = teammateChatMessages
            .filter((message) => message.role !== "tool")
            .map((message) => {
              return toMessageWithStructuredContent(message);
            });

          // Reset the list of messages with the data coming from the server.
          setMessages(messages);
        } catch (error) {
          console.error("Failed to fetch teammate copilot messages", error);
        }
      }
    };

    fetchMessages();
  }, [fetchTeammateCopilotMessages, currentTeammateId, gradingId]);

  const quotes = useMemo(() => {
    return messages.flatMap((item) => {
      if (item.role === "user") {
        return [];
      }

      return item.quotes;
    });
  }, [messages]);

  const quoteMatcher = useMemo(() => {
    return QuoteMatcher.new(sortedRecordings);
  }, [sortedRecordings]);

  const quoteMatches = useMemo(() => {
    return quoteMatcher.findMatches(quotes);
  }, [quoteMatcher, quotes]);

  const handleMessageSend = (message: string) => {
    // Enqueue the user message for chat completion
    setCompletionRequests((prev) => [
      ...prev,
      {
        type: "user_message",
        message: {
          id: uuidv4(),
          role: "user",
          createdAt: new Date().toISOString(),
          content: message,
          isSaving: true,
        },
      },
    ]);
    setComposerValue("");
  };

  const handleMessageUpdate = (
    message: ClientChatMessageWithStructuredContent,
  ) => {
    // Upsert the message
    setMessages((prev) => {
      const index = prev.findIndex((m) => m.id === message.id);
      if (index === -1) {
        return [...prev, message];
      }

      const updatedMessages = [...prev];
      updatedMessages[index] = message;
      return updatedMessages;
    });
  };

  const handleMessageRetry = (messageId: string) => {
    // Find the message
    const message = messages.find((m) => m.id === messageId);
    if (!message) {
      return;
    }

    // Remove it from the list
    setMessages((prev) => prev.filter((m) => m.id !== messageId));

    // Requeue a completion request
    switch (message.role) {
      case "user":
        setCompletionRequests((prev) => [
          ...prev,
          {
            type: "user_message",
            message: {
              ...message,
              isSaving: true,
              isError: false,
            },
          },
        ]);
        break;
    }
  };

  // Handle chat completion requests
  const currentCompletionRequest = completionRequests.at(0);
  useEffect(() => {
    if (!currentTeammate || !currentCompletionRequest || !gradingId) {
      return;
    }

    const userMessage =
      currentCompletionRequest.type === "user_message"
        ? currentCompletionRequest.message
        : null;

    const performCompletion = async (signal: AbortSignal) => {
      const authorizationHeader = await getApiAuthorizationHeaderValue();

      const url = new URL(
        `/teammates/${currentTeammate.id}/copilot`,
        apiBaseUrl,
      );

      const updatedMessages = fetchChatCompletion(url, {
        headers: {
          Authorization: authorizationHeader,
          "x-alpharun-commit-sha": __COMMIT_SHA__,
        },
        body: {
          // We proactively generate an assistant message when starting/resuming the lesson
          user_chat_message: userMessage
            ? {
                id: userMessage.id,
                content: userMessage.content,
              }
            : null,
          chat_details: {
            type: "grading",
            grading_id: gradingId,
          },
        },
        signal,
      });

      for await (const message of updatedMessages) {
        // Once we receive an assistant message, make the user message permanent
        if (userMessage) {
          handleMessageUpdate(
            toMessageWithStructuredContent({
              ...userMessage,
              isSaving: false,
            }),
          );
        }

        if (message.role !== "tool") {
          handleMessageUpdate(toMessageWithStructuredContent(message));
        }
      }
    };

    const abortController = new AbortController();
    performCompletion(abortController.signal).then(
      () => {
        // Dequeue the completion request
        setCompletionRequests((prev) =>
          prev.filter((req) => req !== currentCompletionRequest),
        );
      },
      (error) => {
        // If the effect was canceled, keep the completion request for next render (mostly happens in React strict mode)
        if (error.name === "AbortError") {
          return;
        }

        // Add an error message to the list
        if (userMessage) {
          handleMessageUpdate(
            toMessageWithStructuredContent({
              ...userMessage,
              isSaving: false,
              isError: true,
            }),
          );
        }

        // Dequeue the completion request
        setCompletionRequests((prev) =>
          prev.filter((req) => req !== currentCompletionRequest),
        );

        Sentry.captureException(error);
      },
    );

    return () => {
      abortController.abort();
    };
  }, [currentTeammate, currentCompletionRequest, gradingId]);

  const visibleMessages = useMemo(() => {
    // Filter out empty messages
    const nonEmptyMessages = messages.filter((message) => !!message.content);

    // Show user messages that are still in the queue
    const pendingUserMessages = completionRequests
      .map((req): ClientChatMessageWithStructuredContent | undefined =>
        req.type === "user_message" &&
        !messages.some((m) => m.id === req.message.id)
          ? req.message
          : undefined,
      )
      .filter(isNotUndefined);

    return [...nonEmptyMessages, ...pendingUserMessages];
  }, [messages, completionRequests]);

  // Show a typing indicator until we have a partial assistant message
  const lastMessage = messages.at(-1);
  const isLastMessageLoading =
    lastMessage?.role === "assistant" &&
    lastMessage.isLoading &&
    lastMessage.content;
  const isLoading = completionRequests.length > 0 && !isLastMessageLoading;

  return {
    visibleMessages,
    quoteMatches,
    isLoading,
    composerValue,
    setComposerValue,
    onMessageSend: handleMessageSend,
    onMessageRetry: handleMessageRetry,
  };
}

function toMessageWithStructuredContent(
  message: ClientUserChatMessage | ClientAssistantChatMessage,
): ClientChatMessageWithStructuredContent {
  if (message.role === "user") {
    return message;
  }

  const structuredContent = parseStructuredContentFromModelOutput(
    message.isLoading ? trimPartialTags(message.content) : message.content,
    () => undefined,
  );
  const quotes = collectQuotesInStructuredContent(structuredContent).map(
    (quote, index): QuoteParagraph => {
      const text = stringifyStructuredContentPhrasingRunAsPlainText(
        quote.children,
      );
      return {
        type: "quote",
        id: `${message.id}-quote-${index}`,
        short: text,
        long: text,
      };
    },
  );

  return { ...message, structuredContent, quotes };
}
